import { expectArray, expectBoolean, expectNullable, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const LESSON_KINDS = ["regular", "shortTerm"];
export const LESSON_STATUSES = ["draft", "active", "paused", "ended", "archived"];
export const RECURRING_SLOT_STATUSES = ["active", "paused", "ended"];
export const RECURRENCE_PRESETS = [
    "weekly",
    "monthlyWeeks1To4",
    "monthlyWeeks1And3",
    "monthlyWeeks2And4",
    "monthlyWeekOfStartDate",
    "irregular"
];
export const LEGACY_RECURRENCE_PRESET_ALIASES = [
    "monthlyMax4",
    "monthlyTwice13",
    "monthlyTwice24",
    "monthlyOnce"
];
export const RECURRENCE_PRESET_LABELS = {
    weekly: "毎週",
    monthlyWeeks1To4: "第1〜第4週",
    monthlyWeeks1And3: "第1・第3週",
    monthlyWeeks2And4: "第2・第4週",
    monthlyWeekOfStartDate: "開始日の週のみ",
    irregular: "不定期"
};
export function normalizeRecurrencePresetAlias(value) {
    switch (value) {
        case "monthlyMax4":
            return "monthlyWeeks1To4";
        case "monthlyTwice13":
            return "monthlyWeeks1And3";
        case "monthlyTwice24":
            return "monthlyWeeks2And4";
        case "monthlyOnce":
            return "monthlyWeekOfStartDate";
        default:
            return value;
    }
}
export function getRecurrencePresetLabel(recurrencePreset) {
    return RECURRENCE_PRESET_LABELS[recurrencePreset];
}
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
function expectInteger(value, path) {
    const parsed = expectNumber(value, path);
    if (!Number.isInteger(parsed)) {
        throw new Error(`${path} must be an integer`);
    }
    return parsed;
}
function parseOptionalStringArray(value, path) {
    if (value === null || value === undefined) {
        return [];
    }
    return expectArray(value, expectString, path);
}
function parseWeekOfMonthSet(value, path) {
    if (value === null || value === undefined) {
        return null;
    }
    return expectArray(value, expectInteger, path);
}
export function parseLessonKind(value, path = "lessonKind") {
    return parseStringEnum(value, LESSON_KINDS, path);
}
export function parseLessonStatus(value, path = "lessonStatus") {
    return parseStringEnum(value, LESSON_STATUSES, path);
}
export function parseRecurringSlotStatus(value, path = "recurringSlotStatus") {
    return parseStringEnum(value, RECURRING_SLOT_STATUSES, path);
}
export function parseRecurrencePreset(value, path = "recurrencePreset") {
    const parsed = expectString(value, path);
    if (!RECURRENCE_PRESETS.includes(parsed) &&
        !LEGACY_RECURRENCE_PRESET_ALIASES.includes(parsed)) {
        throw new Error(`${path} must be one of: ${[...RECURRENCE_PRESETS, ...LEGACY_RECURRENCE_PRESET_ALIASES].join(", ")}`);
    }
    return normalizeRecurrencePresetAlias(parsed);
}
function parseStudioDecisionStatus(value, path) {
    const parsed = expectString(value, path);
    if (parsed !== "confirmed" && parsed !== "tbd") {
        throw new Error(`${path} must be one of: confirmed, tbd`);
    }
    return parsed;
}
function parseTeacherAssignmentStatus(value, path) {
    const parsed = expectString(value, path);
    if (parsed !== "assigned" && parsed !== "tbd") {
        throw new Error(`${path} must be one of: assigned, tbd`);
    }
    return parsed;
}
function parseCreateLessonWeekdaySlot(value, path) {
    const record = expectRecord(value, path);
    const weekday = expectInteger(record.weekday, `${path}.weekday`);
    if (weekday < 0 || weekday > 6) {
        throw new Error(`${path}.weekday must be an integer from 0 to 6`);
    }
    return {
        weekday,
        startTime: expectString(record.startTime, `${path}.startTime`),
        endTime: expectString(record.endTime, `${path}.endTime`),
        studioId: expectOptionalString(record.studioId, `${path}.studioId`),
        studioDecisionStatus: parseStudioDecisionStatus(record.studioDecisionStatus, `${path}.studioDecisionStatus`),
        teacherPlatformUserIds: parseOptionalStringArray(record.teacherPlatformUserIds, `${path}.teacherPlatformUserIds`),
        teacherAssignmentStatus: parseTeacherAssignmentStatus(record.teacherAssignmentStatus, `${path}.teacherAssignmentStatus`)
    };
}
export function parseAdminLessonOccurrenceSummary(value, path = "adminLessonOccurrenceSummary") {
    const record = expectRecord(value, path);
    return {
        occurrenceId: expectString(record.occurrenceId, `${path}.occurrenceId`),
        occurrenceDate: expectString(record.occurrenceDate, `${path}.occurrenceDate`),
        startAt: expectString(record.startAt, `${path}.startAt`),
        endAt: expectString(record.endAt, `${path}.endAt`),
        sourceType: expectString(record.sourceType, `${path}.sourceType`),
        syncState: expectString(record.syncState, `${path}.syncState`),
        lifecycleStatus: expectString(record.lifecycleStatus, `${path}.lifecycleStatus`),
        studioNameSnapshot: expectOptionalString(record.studioNameSnapshot, `${path}.studioNameSnapshot`),
        teacherNameSnapshots: parseOptionalStringArray(record.teacherNameSnapshots, `${path}.teacherNameSnapshots`)
    };
}
export function parseAdminRecurringSlot(value, path = "adminRecurringSlot") {
    const record = expectRecord(value, path);
    return {
        slotId: expectString(record.slotId, `${path}.slotId`),
        revision: expectNumber(record.revision, `${path}.revision`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        weekday: expectInteger(record.weekday, `${path}.weekday`),
        startTime: expectString(record.startTime, `${path}.startTime`),
        endTime: expectString(record.endTime, `${path}.endTime`),
        activeFrom: expectString(record.activeFrom, `${path}.activeFrom`),
        activeTo: expectString(record.activeTo, `${path}.activeTo`),
        recurrencePreset: parseRecurrencePreset(record.recurrencePreset, `${path}.recurrencePreset`),
        weekOfMonthSet: parseWeekOfMonthSet(record.weekOfMonthSet, `${path}.weekOfMonthSet`),
        studioId: expectOptionalString(record.studioId, `${path}.studioId`),
        studioDecisionStatus: expectOptionalString(record.studioDecisionStatus, `${path}.studioDecisionStatus`),
        defaultTeacherPlatformUserIds: parseOptionalStringArray(record.defaultTeacherPlatformUserIds, `${path}.defaultTeacherPlatformUserIds`),
        teacherAssignmentStatus: expectOptionalString(record.teacherAssignmentStatus, `${path}.teacherAssignmentStatus`),
        teacherNameSnapshots: parseOptionalStringArray(record.teacherNameSnapshots, `${path}.teacherNameSnapshots`),
        studioNameSnapshot: expectOptionalString(record.studioNameSnapshot, `${path}.studioNameSnapshot`),
        status: parseRecurringSlotStatus(record.status, `${path}.status`),
        affectedYearMonths: parseOptionalStringArray(record.affectedYearMonths, `${path}.affectedYearMonths`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseAdminRecurringSlotsResponse(value, path = "adminRecurringSlotsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminRecurringSlot, `${path}.items`)
    };
}
export function parseAdminLessonListItem(value, path = "adminLessonListItem") {
    const record = expectRecord(value, path);
    return {
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonCode: expectString(record.lessonCode, `${path}.lessonCode`),
        title: expectString(record.title, `${path}.title`),
        shortTitle: expectOptionalString(record.shortTitle, `${path}.shortTitle`),
        lessonKind: parseLessonKind(record.lessonKind, `${path}.lessonKind`),
        status: parseLessonStatus(record.status, `${path}.status`),
        publicVisible: expectBoolean(record.publicVisible, `${path}.publicVisible`),
        startDate: expectString(record.startDate, `${path}.startDate`),
        endDate: expectString(record.endDate, `${path}.endDate`),
        defaultStudioId: expectOptionalString(record.defaultStudioId, `${path}.defaultStudioId`),
        defaultStudioName: expectOptionalString(record.defaultStudioName, `${path}.defaultStudioName`),
        primaryTeacherPlatformUserIds: parseOptionalStringArray(record.primaryTeacherPlatformUserIds, `${path}.primaryTeacherPlatformUserIds`),
        primaryTeacherNameSnapshots: parseOptionalStringArray(record.primaryTeacherNameSnapshots, `${path}.primaryTeacherNameSnapshots`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseAdminLessonsResponse(value, path = "adminLessonsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminLessonListItem, `${path}.items`)
    };
}
export function parseAdminLessonDetail(value, path = "adminLessonDetail") {
    const record = expectRecord(value, path);
    return {
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        revision: expectNumber(record.revision, `${path}.revision`),
        lessonCode: expectString(record.lessonCode, `${path}.lessonCode`),
        title: expectString(record.title, `${path}.title`),
        shortTitle: expectOptionalString(record.shortTitle, `${path}.shortTitle`),
        description: expectOptionalString(record.description, `${path}.description`),
        genre: expectOptionalString(record.genre, `${path}.genre`),
        targetLabel: expectOptionalString(record.targetLabel, `${path}.targetLabel`),
        lessonKind: parseLessonKind(record.lessonKind, `${path}.lessonKind`),
        startDate: expectString(record.startDate, `${path}.startDate`),
        endDate: expectString(record.endDate, `${path}.endDate`),
        defaultStartTime: expectOptionalString(record.defaultStartTime, `${path}.defaultStartTime`),
        defaultEndTime: expectOptionalString(record.defaultEndTime, `${path}.defaultEndTime`),
        defaultStudioId: expectOptionalString(record.defaultStudioId, `${path}.defaultStudioId`),
        defaultStudioName: expectOptionalString(record.defaultStudioName, `${path}.defaultStudioName`),
        defaultStudioDecisionStatus: expectNullable(record.defaultStudioDecisionStatus, parseStudioDecisionStatus, `${path}.defaultStudioDecisionStatus`),
        defaultTeacherPlatformUserIds: parseOptionalStringArray(record.defaultTeacherPlatformUserIds, `${path}.defaultTeacherPlatformUserIds`),
        defaultTeacherAssignmentStatus: expectNullable(record.defaultTeacherAssignmentStatus, parseTeacherAssignmentStatus, `${path}.defaultTeacherAssignmentStatus`),
        trialPolicyId: expectOptionalString(record.trialPolicyId, `${path}.trialPolicyId`),
        visibilityPolicyId: expectOptionalString(record.visibilityPolicyId, `${path}.visibilityPolicyId`),
        publicVisible: expectBoolean(record.publicVisible, `${path}.publicVisible`),
        status: parseLessonStatus(record.status, `${path}.status`),
        sortOrder: expectNumber(record.sortOrder, `${path}.sortOrder`),
        primaryTeacherPlatformUserIds: parseOptionalStringArray(record.primaryTeacherPlatformUserIds, `${path}.primaryTeacherPlatformUserIds`),
        primaryTeacherNameSnapshots: parseOptionalStringArray(record.primaryTeacherNameSnapshots, `${path}.primaryTeacherNameSnapshots`),
        recurringSlots: expectArray(record.recurringSlots, parseAdminRecurringSlot, `${path}.recurringSlots`),
        manualOccurrences: expectArray(record.manualOccurrences, parseAdminLessonOccurrenceSummary, `${path}.manualOccurrences`),
        affectedYearMonths: parseOptionalStringArray(record.affectedYearMonths, `${path}.affectedYearMonths`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseCreateAdminLessonRequest(value, path = "createAdminLessonRequest") {
    const record = expectRecord(value, path);
    return {
        title: expectString(record.title, `${path}.title`),
        shortTitle: expectOptionalString(record.shortTitle, `${path}.shortTitle`),
        description: expectOptionalString(record.description, `${path}.description`),
        genre: expectOptionalString(record.genre, `${path}.genre`),
        targetLabel: expectOptionalString(record.targetLabel, `${path}.targetLabel`),
        lessonKind: parseLessonKind(record.lessonKind, `${path}.lessonKind`),
        publicVisible: expectBoolean(record.publicVisible, `${path}.publicVisible`),
        trialPolicyId: expectOptionalString(record.trialPolicyId, `${path}.trialPolicyId`),
        visibilityPolicyId: expectOptionalString(record.visibilityPolicyId, `${path}.visibilityPolicyId`),
        startDate: expectOptionalString(record.startDate, `${path}.startDate`),
        defaultStartTime: expectOptionalString(record.defaultStartTime, `${path}.defaultStartTime`),
        defaultEndTime: expectOptionalString(record.defaultEndTime, `${path}.defaultEndTime`),
        startTime: expectString(record.startTime, `${path}.startTime`),
        endTime: expectString(record.endTime, `${path}.endTime`),
        defaultStudioId: expectOptionalString(record.defaultStudioId, `${path}.defaultStudioId`),
        studioId: expectOptionalString(record.studioId, `${path}.studioId`),
        defaultStudioDecisionStatus: expectNullable(record.defaultStudioDecisionStatus, parseStudioDecisionStatus, `${path}.defaultStudioDecisionStatus`),
        studioTbd: record.studioTbd === true,
        defaultTeacherPlatformUserIds: parseOptionalStringArray(record.defaultTeacherPlatformUserIds, `${path}.defaultTeacherPlatformUserIds`),
        teacherPlatformUserIds: parseOptionalStringArray(record.teacherPlatformUserIds, `${path}.teacherPlatformUserIds`),
        defaultTeacherAssignmentStatus: expectNullable(record.defaultTeacherAssignmentStatus, parseTeacherAssignmentStatus, `${path}.defaultTeacherAssignmentStatus`),
        teacherTbd: record.teacherTbd === true,
        recurrencePreset: expectNullable(record.recurrencePreset, parseRecurrencePreset, `${path}.recurrencePreset`),
        weekdaySlots: record.weekdaySlots === undefined || record.weekdaySlots === null
            ? null
            : expectArray(record.weekdaySlots, parseCreateLessonWeekdaySlot, `${path}.weekdaySlots`),
        firstOccurrenceDate: expectOptionalString(record.firstOccurrenceDate, `${path}.firstOccurrenceDate`),
        lastOccurrenceDate: expectOptionalString(record.lastOccurrenceDate, `${path}.lastOccurrenceDate`)
    };
}
export function parseUpdateAdminLessonRequest(value, path = "updateAdminLessonRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "title")) {
        parsed.title = expectString(record.title, `${path}.title`);
    }
    if (hasOwn(record, "shortTitle")) {
        parsed.shortTitle = expectOptionalString(record.shortTitle, `${path}.shortTitle`);
    }
    if (hasOwn(record, "description")) {
        parsed.description = expectOptionalString(record.description, `${path}.description`);
    }
    if (hasOwn(record, "genre")) {
        parsed.genre = expectOptionalString(record.genre, `${path}.genre`);
    }
    if (hasOwn(record, "targetLabel")) {
        parsed.targetLabel = expectOptionalString(record.targetLabel, `${path}.targetLabel`);
    }
    if (hasOwn(record, "publicVisible")) {
        parsed.publicVisible = expectBoolean(record.publicVisible, `${path}.publicVisible`);
    }
    if (hasOwn(record, "trialPolicyId")) {
        parsed.trialPolicyId = expectOptionalString(record.trialPolicyId, `${path}.trialPolicyId`);
    }
    if (hasOwn(record, "visibilityPolicyId")) {
        parsed.visibilityPolicyId = expectOptionalString(record.visibilityPolicyId, `${path}.visibilityPolicyId`);
    }
    if (hasOwn(record, "sortOrder")) {
        parsed.sortOrder = expectNumber(record.sortOrder, `${path}.sortOrder`);
    }
    return parsed;
}
export function parseTransitionLessonStatusRequest(value, path = "transitionLessonStatusRequest") {
    const record = expectRecord(value, path);
    return {
        toStatus: parseLessonStatus(record.toStatus, `${path}.toStatus`)
    };
}
export function parseCreateRecurringSlotRequest(value, path = "createRecurringSlotRequest") {
    const record = expectRecord(value, path);
    const parsed = {
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        activeFrom: expectString(record.activeFrom, `${path}.activeFrom`),
        activeTo: expectOptionalString(record.activeTo, `${path}.activeTo`),
        startTime: expectString(record.startTime, `${path}.startTime`),
        endTime: expectString(record.endTime, `${path}.endTime`),
        recurrencePreset: parseRecurrencePreset(record.recurrencePreset, `${path}.recurrencePreset`),
        studioId: expectOptionalString(record.studioId, `${path}.studioId`),
        studioTbd: hasOwn(record, "studioTbd") ? expectBoolean(record.studioTbd, `${path}.studioTbd`) : false,
        defaultTeacherPlatformUserIds: parseOptionalStringArray(record.defaultTeacherPlatformUserIds, `${path}.defaultTeacherPlatformUserIds`),
        teacherTbd: hasOwn(record, "teacherTbd") ? expectBoolean(record.teacherTbd, `${path}.teacherTbd`) : false
    };
    if (hasOwn(record, "weekday")) {
        parsed.weekday = expectInteger(record.weekday, `${path}.weekday`);
    }
    if (hasOwn(record, "studioDecisionStatus")) {
        parsed.studioDecisionStatus = expectNullable(record.studioDecisionStatus, (entry) => parseStudioDecisionStatus(entry, `${path}.studioDecisionStatus`), `${path}.studioDecisionStatus`);
    }
    if (hasOwn(record, "teacherAssignmentStatus")) {
        parsed.teacherAssignmentStatus = expectNullable(record.teacherAssignmentStatus, (entry) => parseTeacherAssignmentStatus(entry, `${path}.teacherAssignmentStatus`), `${path}.teacherAssignmentStatus`);
    }
    if (hasOwn(record, "status")) {
        parsed.status = parseRecurringSlotStatus(record.status, `${path}.status`);
    }
    return parsed;
}
export function parseUpdateRecurringSlotRequest(value, path = "updateRecurringSlotRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "activeFrom")) {
        parsed.activeFrom = expectString(record.activeFrom, `${path}.activeFrom`);
    }
    if (hasOwn(record, "weekday")) {
        parsed.weekday = expectInteger(record.weekday, `${path}.weekday`);
    }
    if (hasOwn(record, "activeTo")) {
        parsed.activeTo = expectOptionalString(record.activeTo, `${path}.activeTo`);
    }
    if (hasOwn(record, "startTime")) {
        parsed.startTime = expectString(record.startTime, `${path}.startTime`);
    }
    if (hasOwn(record, "endTime")) {
        parsed.endTime = expectString(record.endTime, `${path}.endTime`);
    }
    if (hasOwn(record, "recurrencePreset")) {
        parsed.recurrencePreset = parseRecurrencePreset(record.recurrencePreset, `${path}.recurrencePreset`);
    }
    if (hasOwn(record, "studioId")) {
        parsed.studioId = expectOptionalString(record.studioId, `${path}.studioId`);
    }
    if (hasOwn(record, "studioDecisionStatus")) {
        parsed.studioDecisionStatus = expectNullable(record.studioDecisionStatus, (entry) => parseStudioDecisionStatus(entry, `${path}.studioDecisionStatus`), `${path}.studioDecisionStatus`);
    }
    if (hasOwn(record, "studioTbd")) {
        parsed.studioTbd = expectBoolean(record.studioTbd, `${path}.studioTbd`);
    }
    if (hasOwn(record, "defaultTeacherPlatformUserIds")) {
        parsed.defaultTeacherPlatformUserIds = parseOptionalStringArray(record.defaultTeacherPlatformUserIds, `${path}.defaultTeacherPlatformUserIds`);
    }
    if (hasOwn(record, "teacherAssignmentStatus")) {
        parsed.teacherAssignmentStatus = expectNullable(record.teacherAssignmentStatus, (entry) => parseTeacherAssignmentStatus(entry, `${path}.teacherAssignmentStatus`), `${path}.teacherAssignmentStatus`);
    }
    if (hasOwn(record, "teacherTbd")) {
        parsed.teacherTbd = expectBoolean(record.teacherTbd, `${path}.teacherTbd`);
    }
    return parsed;
}
export function parseTransitionRecurringSlotStatusRequest(value, path = "transitionRecurringSlotStatusRequest") {
    const record = expectRecord(value, path);
    return {
        toStatus: parseRecurringSlotStatus(record.toStatus, `${path}.toStatus`)
    };
}
export function parseCreateManualOccurrenceRequest(value, path = "createManualOccurrenceRequest") {
    const record = expectRecord(value, path);
    return {
        occurrenceDate: expectString(record.occurrenceDate, `${path}.occurrenceDate`),
        startTime: expectString(record.startTime, `${path}.startTime`),
        endTime: expectString(record.endTime, `${path}.endTime`),
        studioId: expectOptionalString(record.studioId, `${path}.studioId`),
        studioDecisionStatus: expectNullable(record.studioDecisionStatus, parseStudioDecisionStatus, `${path}.studioDecisionStatus`),
        studioTbd: typeof record.studioTbd === "boolean"
            ? expectBoolean(record.studioTbd, `${path}.studioTbd`)
            : parseStudioDecisionStatus(record.studioDecisionStatus ?? "tbd", `${path}.studioDecisionStatus`) === "tbd",
        teacherPlatformUserIds: parseOptionalStringArray(record.teacherPlatformUserIds, `${path}.teacherPlatformUserIds`),
        teacherAssignmentStatus: expectNullable(record.teacherAssignmentStatus, parseTeacherAssignmentStatus, `${path}.teacherAssignmentStatus`),
        teacherTbd: typeof record.teacherTbd === "boolean"
            ? expectBoolean(record.teacherTbd, `${path}.teacherTbd`)
            : parseTeacherAssignmentStatus(record.teacherAssignmentStatus ?? "tbd", `${path}.teacherAssignmentStatus`) === "tbd",
        publicNote: expectOptionalString(record.publicNote, `${path}.publicNote`),
        operatorNote: expectOptionalString(record.operatorNote, `${path}.operatorNote`),
        adminNote: expectOptionalString(record.adminNote, `${path}.adminNote`)
    };
}
export function parseCreateManualOccurrencesResponse(value, path = "createManualOccurrencesResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminLessonOccurrenceSummary, `${path}.items`),
        affectedYearMonths: parseOptionalStringArray(record.affectedYearMonths, `${path}.affectedYearMonths`)
    };
}
